import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Building2, Loader2, HelpCircle, RefreshCw, Settings, Sun, Moon, MapPin, BookOpen, Megaphone, ArrowLeftRight } from 'lucide-react';
import { useKeycloak } from '../../../contexts/KeycloakContext';
import { useCustomer } from '../../../contexts/CustomerContext';
import { useOperationalSettings } from '../../../contexts/OperationalSettingsContext';
import { cn } from '../../../utils/cn';
import { searchIncludes } from '../../../utils/textNormalization';
import { Condo } from '../types';
import { ConnectionStatusIndicatorDarkWithDropdown } from '../../ConnectionStatusIndicator';
import { isElectron, isMac, isWindows, windowControls } from '../../../utils/platform';
import { ConnectionStatus as WsConnectionStatus, WsStats } from '../../../services/visitorWebSocketClient';
import { PersonalSettingsModal } from './PersonalSettingsModal';
import { CustomerSettingsModal } from './CustomerSettingsModal';
import { WindowControls } from '../../WindowControls';
import { useUserProfilePicture } from '../../../hooks/useUserProfilePicture';
import { makeAbsolutePhotoUrl } from '../../../services/api';

interface TopBarProps {
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  selectedCondo: string;
  setSelectedCondo: (condo: string) => void;
  condoSelectorOpen: boolean;
  setCondoSelectorOpen: (open: boolean) => void;
  condoSearchTerm: string;
  setCondoSearchTerm: (term: string) => void;
  allCondos: Condo[];
  openTabs: string[];
  setOpenTabs: (tabs: string[]) => void;
  onExitOperationalMode: () => void;
  // New props for real data
  isLoading?: boolean;
  isConnected?: boolean;
  onSelectCondo?: (condo: Condo) => void;
  // When true, shows read-only customer name instead of dropdown selector
  singleCustomerMode?: boolean;
  isSearching?: boolean;
  // Debug panel control
  showDebugPanel?: boolean;
  onToggleDebugPanel?: () => void;
  // Expired visitors control
  onOpenExpiredVisitors?: () => void;
  // Device audit control
  onOpenDeviceAudit?: () => void;
  // Event stream stats
  eventCount?: number;
  lastEventDescription?: string;
  lastEventTime?: string;
  // WebSocket connection status for visitantes ativos
  wsConnectionStatus?: WsConnectionStatus;
  getWsStats?: () => WsStats | null;
  // Zone occupancy
  timeSpentEnabled?: boolean;
  onShowZoneOccupancy?: () => void;
  onShowAvisos?: () => void;
  onShowHelpCenter?: () => void;
  onShowManuais?: () => void;
}

const TopBarComponent: React.FC<TopBarProps> = ({
  menuOpen,
  setMenuOpen,
  selectedCondo,
  setSelectedCondo,
  condoSelectorOpen,
  setCondoSelectorOpen,
  condoSearchTerm,
  setCondoSearchTerm,
  allCondos,
  openTabs,
  setOpenTabs,
  onExitOperationalMode,
  isLoading = false,
  isConnected = true,
  onSelectCondo,
  singleCustomerMode = false,
  isSearching = false,
  showDebugPanel = false,
  onToggleDebugPanel,
  onOpenExpiredVisitors,
  onOpenDeviceAudit,
  eventCount,
  lastEventDescription,
  lastEventTime,
  wsConnectionStatus,
  getWsStats,
  timeSpentEnabled = false,
  onShowZoneOccupancy,
  onShowAvisos,
  onShowHelpCenter,
  onShowManuais,
}) => {
  const navigate = useNavigate();
  const { user, logout, hasAnyRole } = useKeycloak();
  const { operationalRoles, openRolePicker } = useCustomer();
  const { settings: opSettings, updateSettings: updateOpSettings } = useOperationalSettings();
  const isLightTheme = opSettings.theme === 'light';
  const toggleTheme = () => updateOpSettings({ theme: isLightTheme ? 'dark' : 'light' });
  const currentCondo = allCondos.find(c => c.name === selectedCondo);
  const currentCondoPhotoUrl = currentCondo?.photo_url;
  const currentCondoCode = currentCondo?.internal_code;

  // Get user profile picture
  const { profilePicture } = useUserProfilePicture();

  // Check if user is a porteiro (should be locked in operational mode)
  const isPorteiro = hasAnyRole('porteiro');

  // Check if user is super_admin (needed for debug features)
  const isSuperAdmin = hasAnyRole('super_admin');

  // Local state for current time - updates every second (isolated to this component)
  const [currentTime, setCurrentTime] = useState(new Date());

  // Track window maximized/fullscreen state for macOS traffic lights and Windows controls spacing
  const [isWindowMaximized, setIsWindowMaximized] = useState(false);

  // Personal Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Customer Settings modal state
  const [showCustomerSettingsModal, setShowCustomerSettingsModal] = useState(false);

  // Update available state (same as Layout.tsx)
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    const onAvailable = () => setUpdateAvailable(true);
    const onCleared = () => setUpdateAvailable(false);
    window.addEventListener('panther:update-available', onAvailable);
    window.addEventListener('panther:update-cleared', onCleared);
    return () => {
      window.removeEventListener('panther:update-available', onAvailable);
      window.removeEventListener('panther:update-cleared', onCleared);
    };
  }, []);

  // Debug mode state (only for super_admin in Electron dev, OFF by default)
  const [debugModeEnabled, setDebugModeEnabled] = useState(() => {
    // Only allow if user is super_admin AND explicitly enabled in localStorage
    if (!isSuperAdmin) {
      // Clear any lingering debug mode for non-super_admins
      localStorage.removeItem('exitDetectionDebug');
      return false;
    }
    // Default to false (OFF) - user must explicitly turn it on
    return localStorage.getItem('exitDetectionDebug') === 'true';
  });

  const toggleDebugMode = () => {
    const newValue = !debugModeEnabled;
    setDebugModeEnabled(newValue);
    localStorage.setItem('exitDetectionDebug', String(newValue));
    setMenuOpen(false);
  };

  // Check if debug option should be shown (only for super_admin in Electron dev)
  const showDebugOption = isElectron() && process.env.NODE_ENV === 'development' && isSuperAdmin;

  // Hard refresh handler
  const handleHardRefresh = () => {
    // Force reload with cache bypass
    window.location.reload();
  };

  // F7 key handler for hard refresh
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F7') {
        e.preventDefault();
        handleHardRefresh();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Update clock every second (isolated to TopBar component for performance)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Track window state for both Mac and Windows
    if (!isElectron() || (!isMac() && !isWindows())) return;

    const checkWindowState = async () => {
      const maximized = await windowControls.isMaximized();
      console.log('[TopBar] Window state check:', {
        maximized,
        currentState: isWindowMaximized,
        platform: isMac() ? 'mac' : 'windows'
      });
      setIsWindowMaximized(maximized);
    };

    checkWindowState();

    const handleResize = () => {
      checkWindowState();
    };

    window.addEventListener('resize', handleResize);

    // Listen for fullscreen state changes from Electron via IPC
    const removeWindowStateListener = windowControls.onStateChanged(() => {
      console.log('[TopBar] Window state changed (IPC event)');
      checkWindowState();
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      removeWindowStateListener();
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    const weekday = date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    const year = date.getFullYear();
    // Format: "Ter, 02 Dez 2025"
    return `${weekday}, ${day} ${month} ${year}`;
  };

  const phaseOrder: Record<string, number> = { production: 0, implementation: 1, disabled: 2 };

  const filteredCondos = allCondos
    .filter(condo => !condoSearchTerm.trim() || searchIncludes(condo.name, condoSearchTerm) || searchIncludes(condo.internal_code || '', condoSearchTerm))
    .sort((a, b) => {
      const aPhase = phaseOrder[a.phase || 'disabled'] ?? 2;
      const bPhase = phaseOrder[b.phase || 'disabled'] ?? 2;
      if (aPhase !== bPhase) return aPhase - bPhase;
      return a.name.localeCompare(b.name);
    });

  const handleCondoSelect = (condo: Condo) => {
    if (onSelectCondo) {
      onSelectCondo(condo);
    } else {
      setSelectedCondo(condo.name);
      if (!openTabs.includes(condo.name)) {
        setOpenTabs([...openTabs, condo.name]);
      }
    }
    setCondoSelectorOpen(false);
    setCondoSearchTerm('');
  };

  // Enter key: select first condo in dropdown when open
  useEffect(() => {
    if (!condoSelectorOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && filteredCondos.length > 0) {
        e.preventDefault();
        handleCondoSelect(filteredCondos[0]);
        setCondoSelectorOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [condoSelectorOpen, filteredCondos]);

  return (
    <div className={cn(
      "py-1 border-b flex items-center justify-between gap-2.5",
      "electron-drag",
      isLightTheme
        ? "bg-gray-100 border-gray-300"
        : "bg-gray-950 border-gray-800",
      isMac() ? (isWindowMaximized ? "pl-3" : "pl-24") : "pl-3",
      "pr-3"
    )}>
      {/* Left side - Logo and Menu */}
      <div className="flex items-center gap-2.5">
        {/* Logo and Operational Mode */}
        <div className="flex items-center gap-2">
          <img
            src={`${process.env.PUBLIC_URL || ''}/panther-logo.svg`}
            alt="Panther Logo"
            className={cn("h-7 w-auto", isLightTheme && "brightness-0")}
          />
          <div>
            <p className="text-sm font-display font-bold leading-tight tracking-wide text-white">ACCESS</p>
            <p className="text-[9px] leading-tight text-blue-400">Modo Operacional</p>
          </div>
        </div>

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all border electron-no-drag",
              isLightTheme
                ? "bg-white hover:bg-gray-50 border-gray-300"
                : "bg-gray-900 hover:bg-gray-800 border-gray-800"
            )}
          >
            {updateAvailable && (
              <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-yellow-400 border-2 border-gray-900 z-10" />
            )}
            {profilePicture ? (
              <img
                src={profilePicture}
                alt={user?.name || 'Usuário'}
                className="h-6 w-[19px] rounded-sm object-cover border border-gray-700"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center"
              style={{ display: profilePicture ? 'none' : 'flex' }}
            >
              <span className="text-[10px] font-semibold">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="text-left hidden md:block">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-medium text-gray-200 leading-tight">{user?.name}</p>
                {isSearching && (
                  <Loader2 className="h-3 w-3 text-blue-400 animate-spin flex-shrink-0" />
                )}
              </div>
              <p className="text-[9px] text-gray-500 leading-tight">@{user?.username || user?.email?.split('@')[0] || 'usuario'}</p>
            </div>
            {menuOpen ? <X className="h-3 w-3 text-gray-400" /> : <Menu className="h-3 w-3 text-gray-400" />}
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute top-full mt-2 left-0 w-64 bg-gray-950 rounded-xl shadow-2xl border border-gray-800 z-20 overflow-hidden">
                {timeSpentEnabled && (
                  <button
                    onClick={() => { onShowZoneOccupancy?.(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white transition-colors border-b border-gray-800"
                  >
                    <MapPin className="h-4 w-4 text-green-500" />
                    <span>Quem está dentro</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowSettingsModal(true);
                    setMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white transition-colors border-b border-gray-800"
                >
                  <Settings className="h-4 w-4" />
                  <span>Configurações Pessoais</span>
                </button>
                <button
                  onClick={() => {
                    onShowHelpCenter?.();
                    setMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white transition-colors border-b border-gray-800"
                >
                  <HelpCircle className="h-4 w-4" />
                  <span>Central de Ajuda</span>
                </button>
                <button
                  onClick={() => {
                    handleHardRefresh();
                    setMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white transition-colors border-b border-gray-800"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Recarregar</span>
                  <span className="ml-auto text-xs text-gray-500">F7</span>
                </button>
                {!!(window as any).electronAPI && (
                  <button
                    onClick={() => { (window as any).electronAPI?.triggerUpdateCheck?.(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white transition-colors border-b border-gray-800"
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span>Verificar Atualizações</span>
                    {updateAvailable && (
                      <span className="ml-auto flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-yellow-400" />
                        <span className="text-xs text-yellow-400 font-semibold">Novo</span>
                      </span>
                    )}
                  </button>
                )}
                {!!(window as any).electronAPI && (
                  <button
                    onClick={() => { (window as any).electronAPI?.triggerReleaseNotes?.(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white transition-colors border-b border-gray-800"
                  >
                    <BookOpen className="h-4 w-4" />
                    <span>Sobre</span>
                  </button>
                )}
                <button
                  onClick={() => { onShowAvisos?.(); setMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white transition-colors border-b border-gray-800"
                >
                  <Megaphone className="h-4 w-4" />
                  <span>Avisos e Atualizações</span>
                </button>
                <button
                  onClick={() => { onShowManuais?.(); setMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white transition-colors border-b border-gray-800"
                >
                  <BookOpen className="h-4 w-4" />
                  <span>Manuais</span>
                </button>
                {operationalRoles.length > 1 && (
                  <button
                    onClick={() => { openRolePicker(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white transition-colors border-b border-gray-800"
                  >
                    <ArrowLeftRight className="h-4 w-4" />
                    <span>Trocar Perfil</span>
                  </button>
                )}
                <button
                  onClick={() => { handleLogout(); setMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-900 hover:text-white transition-colors"
                >
                  <span>Sair</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Center - Customer Selector or Read-only display */}
      {singleCustomerMode ? (
        /* Read-only display when user has only 1 customer */
        <div className="flex items-center gap-2.5 px-2">
          {currentCondoPhotoUrl
            ? <img src={makeAbsolutePhotoUrl(currentCondoPhotoUrl)!} alt={selectedCondo} className="h-9 w-9 rounded-lg object-cover ring-2 ring-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.45)] flex-shrink-0" />
            : <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center ring-2 ring-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.45)] flex-shrink-0">
                <Building2 className="h-4 w-4 text-white" />
              </div>
          }
          <div className="flex flex-col min-w-0">
            {currentCondoCode && (
              <span className={cn("text-[10px] font-mono leading-none mb-0.5", isLightTheme ? "text-gray-500" : "text-gray-500")}>
                {currentCondoCode}
              </span>
            )}
            <span className={cn("max-w-[180px] truncate text-sm font-semibold leading-tight", isLightTheme ? "text-gray-900" : "text-gray-100")}>
              {selectedCondo}
            </span>
          </div>
        </div>
      ) : (
        /* Dropdown selector when user has multiple customers */
        <div className="relative">
          <button
            onMouseEnter={() => setCondoSelectorOpen(true)}
            onClick={() => setCondoSelectorOpen(!condoSelectorOpen)}
            className={cn(
              "flex items-center gap-2.5 px-2 py-0 rounded-lg transition-colors electron-no-drag",
              isLightTheme ? "hover:bg-black/5" : "hover:bg-white/5"
            )}
          >
            {currentCondoPhotoUrl
              ? <img src={makeAbsolutePhotoUrl(currentCondoPhotoUrl)!} alt={selectedCondo} className="h-9 w-9 rounded-lg object-cover ring-2 ring-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.45)] flex-shrink-0" />
              : <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center ring-2 ring-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.45)] flex-shrink-0">
                  <Building2 className="h-4 w-4 text-white" />
                </div>
            }
            <div className="flex items-center gap-1.5">
              {currentCondoCode && (
                <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border flex-shrink-0", isLightTheme ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-amber-500/15 text-amber-400 border-amber-500/25")}>
                  {currentCondoCode}
                </span>
              )}
              <span className={cn("max-w-[180px] truncate text-sm font-semibold", isLightTheme ? "text-gray-800" : "text-gray-100")}>
                {selectedCondo || 'Selecione um cliente'}
              </span>
            </div>
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 text-gray-500 animate-spin flex-shrink-0" />
            ) : (
              <svg className={cn("h-3.5 w-3.5 text-gray-500 transition-transform flex-shrink-0", condoSelectorOpen && "rotate-180")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>

          {/* Backdrop — only when open */}
          {condoSelectorOpen && (
            <div className="fixed inset-0 z-40" onClick={() => setCondoSelectorOpen(false)} />
          )}

          {/* Dropdown panel — always mounted so images stay cached; hidden via CSS when closed */}
          <div
            className={cn(
              "absolute top-full mt-2 left-0 w-full md:w-96 max-w-md bg-gray-950 rounded-lg shadow-2xl border border-gray-800 overflow-hidden z-50",
              !condoSelectorOpen && "hidden"
            )}
            onMouseEnter={() => setCondoSelectorOpen(true)}
            onMouseLeave={() => setCondoSelectorOpen(false)}
          >
            <div className="p-3 border-b border-gray-800 bg-black">
              <input
                type="text"
                placeholder="Buscar cliente..."
                value={condoSearchTerm}
                onChange={(e) => setCondoSearchTerm(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 text-gray-200 text-sm rounded-lg border border-gray-800 focus:outline-none focus:border-blue-500 placeholder-gray-600"
              />
            </div>

            <div className="max-h-96 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {isLoading ? (
                <div className="px-4 py-8 text-center">
                  <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Carregando clientes...</p>
                </div>
              ) : filteredCondos.length > 0 ? (
                filteredCondos.map((condo, index) => (
                  <button
                    key={condo.id}
                    onClick={() => handleCondoSelect(condo)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-900 transition-colors border-b border-gray-800/50",
                      selectedCondo === condo.name && "bg-blue-600/10"
                    )}
                  >
                    <div className="relative flex-shrink-0">
                      <div className="w-7 h-7 rounded-lg overflow-hidden flex items-center justify-center bg-gray-800">
                        {condo.photo_url
                          ? <img src={makeAbsolutePhotoUrl(condo.photo_url)!} alt={condo.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                          : <Building2 className="h-3.5 w-3.5 text-gray-500" />}
                      </div>
                      <span className={cn(
                        "absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-gray-950",
                        condo.phase === 'production' ? 'bg-green-500' : condo.phase === 'implementation' ? 'bg-amber-400' : 'bg-gray-600'
                      )} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {condo.internal_code && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-gray-700 text-gray-300 border border-gray-600 flex-shrink-0">
                            {condo.internal_code}
                          </span>
                        )}
                        <p className="text-sm font-medium text-gray-200 truncate">{condo.name}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs text-gray-500 capitalize">{condo.type}</p>
                        {condo.portaria_type && condo.portaria_type !== 'none' && (
                          <span className={cn(
                            "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border",
                            condo.portaria_type === 'presencial' ? 'bg-blue-500/15 text-blue-400 border-blue-500/25' :
                            condo.portaria_type === 'autonoma' ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/25' :
                            'bg-teal-500/15 text-teal-400 border-teal-500/25'
                          )}>
                            {condo.portaria_type === 'presencial' ? 'Presencial' : condo.portaria_type === 'autonoma' ? 'Autônoma' : 'Virtual'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-xs text-gray-600">{condo.lastAccess}</span>
                      {index === 0 && (
                        <kbd className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold bg-gray-800 text-gray-400 rounded border border-gray-700">
                          ↵
                        </kbd>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-8 text-center text-gray-600 text-sm">
                  Nenhum cliente encontrado
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Right side - Time, status, and window controls */}
      <div className="flex items-center gap-2.5">
        <div className="text-right">
          <p className={cn("text-sm md:text-base font-bold leading-tight", isLightTheme ? "text-gray-800" : "text-gray-200")}>{formatTime(currentTime)}</p>
          <p className={cn("text-[10px] md:text-xs capitalize leading-tight", isLightTheme ? "text-gray-500" : "text-gray-400")}>{formatDate(currentTime)}</p>
        </div>
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          title={isLightTheme ? 'Mudar para tema escuro' : 'Mudar para tema claro'}
          className={cn(
            "p-1.5 rounded-lg transition-colors electron-no-drag",
            isLightTheme
              ? "text-gray-500 hover:text-gray-800 hover:bg-gray-200"
              : "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
          )}
        >
          {isLightTheme ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        </button>

        <ConnectionStatusIndicatorDarkWithDropdown
          size="sm"
          eventCount={eventCount}
          lastEventDescription={lastEventDescription}
          lastEventTime={lastEventTime}
          wsConnectionStatus={wsConnectionStatus}
          getWsStats={getWsStats}
        />

        {/* Windows window controls */}
        {isWindows() && (
          <WindowControls
            isMaximized={isWindowMaximized}
            variant={isLightTheme ? 'light' : 'dark'}
            className="electron-no-drag ml-1"
          />
        )}
      </div>

      {/* Personal Settings Modal */}
      <PersonalSettingsModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      {/* Customer Settings Modal */}
      {selectedCondo && (
        <CustomerSettingsModal
          open={showCustomerSettingsModal}
          onClose={() => setShowCustomerSettingsModal(false)}
          customerId={allCondos.find(c => c.name === selectedCondo)?.id || ''}
          customerName={selectedCondo}
        />
      )}

    </div>
  );
};

// Wrap in React.memo for performance - prevent re-renders when props haven't changed
export const TopBar = React.memo(TopBarComponent);
